<template>
  <v-container fluid class="workload">
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <div>
        <h1 class="text-h5 font-weight-medium">{{ t('workload.title') }}</h1>
        <div class="text-body-2 text-medium-emphasis">{{ t('workload.description') }}</div>
      </div>
      <v-spacer />
      <v-select
        v-model="typeFilter"
        :items="typeItems"
        item-title="title"
        item-value="value"
        :label="t('workload.typeFilter')"
        density="compact"
        variant="outlined"
        hide-details
        style="max-width: 240px;"
      ></v-select>
      <v-btn
        variant="text"
        prepend-icon="mdi-refresh"
        :loading="loading"
        @click="reload"
      >
        {{ t('dashboard.refresh') }}
      </v-btn>
    </div>

    <v-alert
      v-if="!hasRoleScopes"
      type="info"
      variant="tonal"
      border="start"
      class="mb-4"
    >
      {{ t('workload.noScopes') }}
    </v-alert>

    <v-alert v-if="loadError" type="error" variant="tonal" class="mb-4">
      {{ loadError }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <!-- Una scheda per profilo operativo, più quella delle fasi senza profilo -->
    <div class="d-flex flex-column" style="gap: 16px;">
      <v-card
        v-for="bucket in buckets"
        :key="bucket.key"
        variant="flat"
        border
      >
        <v-card-title class="d-flex align-center flex-wrap py-3" style="gap: 10px;">
          <v-icon :color="bucket.shared ? 'blue-grey' : 'primary'" size="small">
            {{ bucket.shared ? 'mdi-account-multiple-outline' : 'mdi-account-outline' }}
          </v-icon>
          <span class="text-subtitle-1 font-weight-medium">{{ bucket.title }}</span>
          <v-chip v-if="bucket.roleLabel" size="x-small" label variant="tonal" color="deep-purple">
            {{ bucket.roleLabel }}
          </v-chip>

          <v-spacer />

          <div class="d-flex align-center flex-wrap" style="gap: 6px;">
            <v-chip size="small" label variant="tonal">
              {{ t('workload.countProjects', { n: bucket.projects.length }) }}
            </v-chip>
            <v-chip v-if="bucket.overdue" size="small" label variant="tonal" color="error">
              {{ t('workload.countOverdue', { n: bucket.overdue }) }}
            </v-chip>
            <v-chip v-if="bucket.dueToday" size="small" label variant="tonal" color="warning">
              {{ t('workload.countToday', { n: bucket.dueToday }) }}
            </v-chip>
            <v-chip v-if="bucket.pendingVerifications" size="small" label variant="tonal" color="cyan">
              {{ t('workload.countVerifications', { n: bucket.pendingVerifications }) }}
            </v-chip>
          </div>
        </v-card-title>

        <v-divider />

        <!-- Profilo senza nessuna fase: non è una scheda vuota, è una configurazione da sistemare -->
        <v-card-text v-if="bucket.noStatuses" class="py-3">
          <v-alert type="warning" variant="tonal" density="compact">
            {{ t('workload.noStatusesForRole', { role: bucket.roleLabel || bucket.title }) }}
          </v-alert>
        </v-card-text>

        <v-card-text v-else-if="!bucket.projects.length" class="py-3 text-body-2 text-medium-emphasis">
          {{ t('workload.emptyBucket') }}
        </v-card-text>

        <v-data-table
          v-else
          density="compact"
          :headers="headers"
          :items="bucket.projects"
          :items-per-page="10"
          item-value="idProject"
          hover
          @click:row="openProject"
        >
          <template v-slot:item.activeStatus="{ item }">
            <span class="status-cell">
              <span
                class="status-cell__dot"
                :style="{ backgroundColor: statusOf(item)?.color || '#9e9e9e' }"
              ></span>
              {{ statusOf(item)?.descr || item.activeStatus }}
            </span>
          </template>

          <template v-slot:item.reminderDate="{ item }">
            <span :class="reminderClass(item)">
              {{ formatDate(item.reminderDate) }}
              <span v-if="reminderSuffix(item)" class="text-caption">· {{ reminderSuffix(item) }}</span>
            </span>
          </template>

          <template v-slot:item.todo="{ item }">
            <span v-if="todoInfo(item).terminal" class="text-disabled">—</span>
            <v-icon v-else-if="!todoInfo(item).missing.length" size="small" color="success">
              mdi-check-circle-outline
            </v-icon>
            <v-tooltip v-else location="top" max-width="420" open-delay="300">
              <template v-slot:activator="{ props }">
                <span v-bind="props" class="todo-cell">
                  {{ todoInfo(item).missing.map(f => f.label).join(', ') }}
                </span>
              </template>
              <ul class="pl-4 mb-0">
                <li v-for="f in todoInfo(item).missing" :key="f.name">{{ f.label }}</li>
              </ul>
            </v-tooltip>
          </template>

          <template v-slot:item.verifications="{ item }">
            <span v-if="!pendingVerifications(item).length" class="text-disabled">—</span>
            <div v-else class="d-flex flex-wrap" style="gap: 4px;">
              <v-chip
                v-for="v in pendingVerifications(item)"
                :key="v.id"
                size="x-small"
                label
                variant="tonal"
                color="cyan"
                prepend-icon="mdi-robot-outline"
              >
                {{ v.label || v.id }}
              </v-chip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import { useWorkflowAccess } from '../composables/useWorkflowAccess.js'
import { useFieldRequirements } from '../composables/useFieldRequirements.js'
import { apiCall } from '../utils/api.js'
import { followUpState, overdueDays } from '../utils/followUp.js'
import { rolesOfStatus, PRIVILEGED_ROLES } from '../utils/workflowAccess.js'

const { t } = useI18n()
const router = useRouter()
const projectStore = useProjectStore()
const { roles, roleLabel, hasRoleScopes } = useWorkflowAccess()
const { missingFieldsForNextStatus } = useFieldRequirements()

const users = ref([])
const loading = ref(false)
const loadError = ref('')
const typeFilter = ref(null)

const headers = computed(() => [
  { title: t('projects.table.id'), key: 'idProject', width: '210px' },
  { title: t('projects.table.reference'), key: 'reference', width: '120px' },
  { title: t('projects.table.status'), key: 'activeStatus', width: '190px' },
  { title: t('projects.table.dueDate'), key: 'reminderDate', width: '160px' },
  { title: t('workload.todoColumn'), key: 'todo', sortable: false },
  { title: t('workload.verificationsColumn'), key: 'verifications', sortable: false, width: '220px' }
])

const typeItems = computed(() => [
  { title: t('workload.allTypes'), value: null },
  ...projectStore.projectTypes.map(type => ({ title: type.descr, value: type.id }))
])

// ---- helper sul singolo processo ----

const statusOf = (project) =>
  projectStore.getStatusInfo(String(project.activeStatus), project.projectType)

const todoInfo = (project) => missingFieldsForNextStatus(project)

/** Verifiche AI dello stato corrente ancora da superare: campo di validazione non ancora a true. */
const pendingVerifications = (project) =>
  projectStore.resolveVerifications(statusOf(project))
    .filter(v => v.enabled !== false)
    .filter(v => v.validationField && !project[v.validationField])

const reminderClass = (project) => {
  const state = followUpState(project, statusOf(project))
  if (state === 'overdue') return 'text-red font-weight-bold'
  if (state === 'today') return 'text-orange font-weight-bold'
  return ''
}

const reminderSuffix = (project) => {
  const state = followUpState(project, statusOf(project))
  if (state === 'today') return t('projects.table.dueToday')
  if (state !== 'overdue') return ''
  return t('projects.table.overdueBy', { n: Math.max(1, overdueDays(project)) })
}

const formatDate = (value) => {
  if (!value) return '-'
  const [y, m, d] = value.substring(0, 10).split('-')
  if (!y || !m || !d) return value
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('pt-BR')
}

// ---- raggruppamento ----

/** Processi in gioco: attivi, del tipo selezionato. */
const relevantProjects = computed(() => {
  const list = projectStore.projects || []
  if (typeFilter.value === null) return list
  return list.filter(p => parseInt(p.projectType) === typeFilter.value)
})

/** Utenti che operano una parte del flusso: admin e superadmin lo operano tutto, e non hanno coda. */
const operativeUsers = computed(() =>
  users.value.filter(u => u.role && !PRIVILEGED_ROLES.includes(u.role)))

const statsOf = (projects) => {
  let overdue = 0
  let dueToday = 0
  let pending = 0
  for (const project of projects) {
    const state = followUpState(project, statusOf(project))
    if (state === 'overdue') overdue++
    else if (state === 'today') dueToday++
    pending += pendingVerifications(project).length
  }
  return { overdue, dueToday, pendingVerifications: pending }
}

const byReminder = (a, b) =>
  String(a.reminderDate || '9999').localeCompare(String(b.reminderDate || '9999'))

/**
 * Una scheda per utente operativo, più una finale per le fasi che non dichiarano profili.
 *
 * Un processo finisce nella coda di un utente solo se la sua fase nomina esplicitamente il profilo
 * di quell'utente. Le fasi aperte a tutti stanno nella scheda condivisa: metterle sotto ogni
 * utente le conterebbe N volte e non direbbe a nessuno che tocca a lui.
 */
const buckets = computed(() => {
  const out = []
  const projects = relevantProjects.value

  // Quali profili hanno almeno una fase assegnata: serve a distinguere "coda vuota" da
  // "profilo mai assegnato a nessuna fase", che è un problema di configurazione.
  const rolesWithStatuses = new Set()
  for (const type of projectStore.projectTypes) {
    for (const status of type.statusList || []) {
      rolesOfStatus(status).forEach(r => rolesWithStatuses.add(r))
    }
  }

  for (const user of operativeUsers.value) {
    const mine = projects
      .filter(p => rolesOfStatus(statusOf(p)).includes(user.role))
      .sort(byReminder)

    out.push({
      key: `user:${user.username}`,
      title: user.username,
      roleLabel: roleLabel(user.role),
      shared: false,
      noStatuses: !rolesWithStatuses.has(user.role),
      projects: mine,
      ...statsOf(mine)
    })
  }

  // Profili configurati che nessun utente ha ancora: il lavoro c'è, ma non è di nessuno
  for (const role of roles.value) {
    if (operativeUsers.value.some(u => u.role === role.id)) continue

    const orphan = projects
      .filter(p => rolesOfStatus(statusOf(p)).includes(role.id))
      .sort(byReminder)
    if (!orphan.length) continue

    out.push({
      key: `role:${role.id}`,
      title: t('workload.unassignedRole', { role: role.label }),
      roleLabel: role.label,
      shared: true,
      noStatuses: false,
      projects: orphan,
      ...statsOf(orphan)
    })
  }

  const shared = projects
    .filter(p => rolesOfStatus(statusOf(p)).length === 0)
    .sort(byReminder)

  if (shared.length) {
    out.push({
      key: 'shared',
      title: t('workload.sharedBucket'),
      roleLabel: '',
      shared: true,
      noStatuses: false,
      projects: shared,
      ...statsOf(shared)
    })
  }

  return out
})

// ---- caricamento ----

const openProject = (event, row) => {
  const project = row?.item || event
  if (project?.idProject) {
    router.push({ name: 'dashboard', query: { open: project.idProject } })
  }
}

const reload = async () => {
  loading.value = true
  loadError.value = ''
  try {
    if (!projectStore.configurations?.config) {
      await projectStore.loadConfigurations()
    }
    const [directory] = await Promise.all([
      apiCall('/api/users/directory'),
      projectStore.loadProjects(1, null, true)
    ])
    users.value = directory?.users || []
  } catch (e) {
    loadError.value = e?.message || t('workload.loadError')
  } finally {
    loading.value = false
  }
}

onMounted(reload)
</script>

<style scoped>
.workload {
  padding: 16px;
}

.status-cell {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}

.status-cell__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.todo-cell {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>
